// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-04: "we should have a pop up keyboard
 * instead of one on the same card. In fact ALL keyboards should be pop
 * up"): the headset's one keyboard. It stands on its own plane in front of
 * the viewer, below the eyes, with what is typed at its top, instead of
 * taking the place of the page that asked for it. The Browse search and a
 * pin's new name both type on it, and the page behind it stays in view:
 * Browse narrows its cards with every key.
 *
 * Its keys send the presses the pages' own keys used to send (`key` for
 * the search, `pin-key` for a name), so the rules of each stay where they
 * were (`reduceBrowse`, `reducePinEdit`). Digits are on both: dataset
 * titles carry years and numbers ("PM2.5", "2023").
 */

import type * as THREE from 'three'
import { t } from '../i18n'
import type { VrPanelAction } from './vrSidePanel'
import { PANEL_COLORS, createCanvasPanel, type PanelPainter } from './vrPanelKit'
import { markPanel } from './vrPanelLayout'

/** What the keyboard is open for. */
export interface VrKeyboardSpec {
  /** `search`: Space, ⌫ and Done. `name`: with a hyphen ("Dar es-Salaam"), and Cancel. */
  mode: 'search' | 'name'
  /** The line over the field ("Search", "Rename a pin"). */
  title: string
  text: string
  /** Greyed in the field while nothing is typed. */
  placeholder: string
  /** A dot of colour before the title (the pin's). */
  color?: string
}

/** World size: 1 canvas pixel = 0.5 mm, so a key is 46 mm by 34 mm. */
export const KEYBOARD_WIDTH = 0.56
export const KEYBOARD_CANVAS_W = 1120
export const KEYBOARD_CANVAS_H = 620
export const KEYBOARD_HEIGHT = (KEYBOARD_WIDTH * KEYBOARD_CANVAS_H) / KEYBOARD_CANVAS_W

/** Where it stands when it opens: this far ahead of the eyes, along the gaze on the level, and this far below them. */
export const KEYBOARD_AHEAD_M = 0.55
export const KEYBOARD_BELOW_M = 0.26

const ROWS: Record<VrKeyboardSpec['mode'], readonly string[]> = {
  search: ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'],
  name: ['1234567890', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm-'],
}

const PAD = 40
const KEY_W = 92
const KEY_H = 68
const GAP = 10

/** The press a key sends: the search's own, or a pin name's. */
function keyAction(mode: VrKeyboardSpec['mode'], key: string): VrPanelAction {
  return mode === 'search' ? { kind: 'key', key } : { kind: 'pin-key', key }
}

/** Draw the keyboard for `spec`: its title, the field with what is typed, the keys and the controls. */
export function drawKeyboard(p: PanelPainter<VrPanelAction>, spec: VrKeyboardSpec): void {
  const W = p.w
  p.frame(32)
  let titleX = PAD
  if (spec.color) {
    p.g.beginPath()
    p.g.arc(PAD + 14, 50, 13, 0, Math.PI * 2)
    p.g.fillStyle = spec.color
    p.g.fill()
    titleX += 38
  }
  p.text(spec.title, titleX, 50, { size: 28, weight: 700, color: PANEL_COLORS.textSecondary, maxWidth: W - PAD - titleX })

  // The field: what has been typed and the caret, or the placeholder greyed.
  let y = 84
  const fieldW = W - PAD * 2
  p.box(PAD, y, fieldW, 76, 18, 'rgba(34,197,94,0.1)', PANEL_COLORS.accent, 2)
  if (spec.mode === 'search') p.icon('search', PAD + 40, y + 38, 30, PANEL_COLORS.textSecondary)
  const textX = spec.mode === 'search' ? PAD + 76 : PAD + 26
  p.text(spec.text ? `${spec.text}|` : spec.placeholder, textX, y + 39, {
    size: 32,
    weight: spec.text ? 650 : 500,
    color: spec.text ? PANEL_COLORS.text : PANEL_COLORS.muted,
    maxWidth: fieldW - (textX - PAD) - 110,
  })
  if (spec.text) p.button(W - PAD - 92, y + 10, 84, 56, '', keyAction(spec.mode, 'clear'), { icon: 'close', radius: 14 })
  y += 100

  for (const row of ROWS[spec.mode]) {
    const rowW = row.length * KEY_W + (row.length - 1) * GAP
    let x = (W - rowW) / 2
    for (const ch of row) {
      p.button(x, y, KEY_W, KEY_H, ch.toUpperCase(), keyAction(spec.mode, ch), { size: 30, radius: 12 })
      x += KEY_W + GAP
    }
    y += KEY_H + GAP
  }

  const controls: Array<{ key: string; label: string; w: number; primary?: boolean; icon?: 'backspace' }> = spec.mode === 'search'
    ? [
        { key: 'space', label: t('vr.browse.keySpace'), w: 420 },
        { key: 'back', label: '', w: 140, icon: 'backspace' },
        { key: 'done', label: t('vr.browse.keyDone'), w: 220, primary: true },
      ]
    : [
        { key: 'space', label: t('vr.browse.keySpace'), w: 320 },
        { key: 'back', label: '', w: 130, icon: 'backspace' },
        { key: 'cancel', label: t('vr.data.renameCancel'), w: 240 },
        { key: 'done', label: t('vr.browse.keyDone'), w: 240, primary: true },
      ]
  const ctrlW = controls.reduce((sum, c) => sum + c.w, 0) + GAP * (controls.length - 1)
  let x = (W - ctrlW) / 2
  for (const c of controls) {
    p.button(x, y, c.w, KEY_H, c.label, keyAction(spec.mode, c.key), { size: 26, radius: 12, primary: c.primary, icon: c.icon })
    x += c.w + GAP
  }
}

/**
 * Where the keyboard stands when it opens: ahead of the eyes along the
 * gaze laid flat (looking up or down does not throw it into the floor or
 * the ceiling), and below them, where hands rest.
 */
export function keyboardPosition(
  eye: { readonly x: number; readonly y: number; readonly z: number },
  gaze: { readonly x: number; readonly z: number },
): { x: number; y: number; z: number } {
  let fx = gaze.x
  let fz = gaze.z
  const len = Math.hypot(fx, fz)
  if (len < 1e-5) {
    fx = 0
    fz = -1
  } else {
    fx /= len
    fz /= len
  }
  return { x: eye.x + fx * KEYBOARD_AHEAD_M, y: eye.y - KEYBOARD_BELOW_M, z: eye.z + fz * KEYBOARD_AHEAD_M }
}

export interface VrKeyboardHandle {
  readonly mesh: THREE.Mesh
  /** Open it for `spec` (redrawn only when it changed), or close it with null. */
  set(spec: VrKeyboardSpec | null): void
  isVisible(): boolean
  hitTest(uv: { x: number; y: number }): VrPanelAction | null
  /** How many times it has opened: the session stands it before the eyes once per opening. */
  openings(): number
  dispose(): void
}

export function createVrKeyboard(THREE_: typeof THREE): VrKeyboardHandle {
  // Over the side panel and the dock (10), under the laser dot (12).
  const panel = createCanvasPanel<VrPanelAction>(THREE_, {
    widthM: KEYBOARD_WIDTH,
    heightM: KEYBOARD_HEIGHT,
    canvasW: KEYBOARD_CANVAS_W,
    canvasH: KEYBOARD_CANVAS_H,
    renderOrder: 11,
  })
  panel.mesh.name = 'vr-keyboard'
  panel.mesh.visible = false
  // AYNI (owner on Quest, 2026-11-05): kept off every other panel, but it may stand in front of
  // the dock and the date track while you type, as it did (vrPanelLayout `overAnchors`).
  markPanel(panel.mesh, { width: KEYBOARD_WIDTH, height: KEYBOARD_HEIGHT, overAnchors: true })
  let drawn = ''
  let opened = 0

  return {
    mesh: panel.mesh,
    set(spec) {
      if (!spec) {
        panel.mesh.visible = false
        drawn = ''
        return
      }
      if (!panel.mesh.visible) opened++
      panel.mesh.visible = true
      const sig = JSON.stringify(spec)
      if (sig === drawn) return
      drawn = sig
      panel.draw((p) => drawKeyboard(p, spec))
    },
    isVisible: () => panel.mesh.visible,
    hitTest(uv) {
      return panel.mesh.visible ? panel.hitTest(uv) : null
    },
    openings: () => opened,
    dispose() {
      panel.dispose()
    },
  }
}
